/**
 * Superadmin onboarding signups funnel (ADR-0041 Fase 1).
 *
 * Reads `GET /api/platform/onboarding/signups{,/summary}` — the parked PAID signups
 * (pay-then-provision): a paid signup creates a Stripe Checkout Session and parks its
 * intent in `pending_signups` until the webhook provisions the tenant. This page is the
 * funnel: counts by status + a paginated, filterable list, with a one-click CANCEL that
 * frees the slug an abandoned `pending` checkout still holds. The hashed password is
 * NEVER exposed. A daily reaper purges old terminal rows (backend `hub.billing.reaper`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, TriangleAlert, Users } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type PendingSignup,
  type SignupStatus,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

const PAGE_SIZE = 25;

const STATUS_LABEL: Record<SignupStatus, string> = {
  pending: "In attesa",
  completed: "Completata",
  expired: "Scaduta",
  canceled: "Annullata",
};

const STATUS_TONE: Record<SignupStatus, "warning" | "active" | "neutral" | "danger"> = {
  pending: "warning",
  completed: "active",
  expired: "neutral",
  canceled: "danger",
};

const STATUS_FILTERS: SignupStatus[] = ["pending", "completed", "expired", "canceled"];

export function PlatformOnboardingPage() {
  const qc = useQueryClient();
  const [status, setStatus] = React.useState<SignupStatus | "">("");
  const [page, setPage] = React.useState(1);
  const [confirming, setConfirming] = React.useState<PendingSignup | null>(null);

  // Reset to page 1 whenever the filter changes.
  React.useEffect(() => {
    setPage(1);
  }, [status]);

  const summaryQ = useQuery({
    queryKey: ["platform", "signups", "summary"],
    queryFn: () => platformApi.signupsSummary(),
  });

  const listQ = useQuery({
    queryKey: ["platform", "signups", { status, page }],
    queryFn: () =>
      platformApi.listSignups({ status: status || undefined, page, page_size: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => platformApi.cancelSignup(id),
    onSuccess: () => {
      setConfirming(null);
      qc.invalidateQueries({ queryKey: ["platform", "signups"] });
    },
  });

  const rows = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const funnel = summaryQ.data;

  return (
    <PageContainer>
      <PageHeader
        title="Registrazioni"
        description="Checkout di onboarding parcheggiati (pay-then-provision): in attesa, completati e abbandonati."
      />
      <PageContent className="space-y-4">
        {/* Funnel cards */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus((cur) => (cur === s ? "" : s))}
              className={
                "rounded-lg border p-3 text-left transition " +
                (status === s
                  ? "border-accent bg-accent/5"
                  : "border-border/60 hover:border-border")
              }
              aria-pressed={status === s}
            >
              <div className="text-2xl font-semibold tabular-nums">{funnel ? funnel[s] : "—"}</div>
              <div className="mt-1 text-xs text-muted-foreground">{STATUS_LABEL[s]}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {status && (
            <Button size="sm" variant="ghost" onClick={() => setStatus("")}>
              Mostra tutte
            </Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {total} registrazion{total === 1 ? "e" : "i"}
          </span>
        </div>

        {listQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare le registrazioni"
            description={
              listQ.error instanceof PlatformApiError ? listQ.error.message : "Errore di rete."
            }
            action={
              <Button variant="ghost" onClick={() => listQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Users}
            title="Nessuna registrazione"
            description="I checkout di onboarding appariranno qui."
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border/60">
              <Table>
                <Thead>
                  <Tr>
                    <Th>Workspace</Th>
                    <Th>Tipo</Th>
                    <Th>Azienda</Th>
                    <Th>Email</Th>
                    <Th>Piano</Th>
                    <Th>Stato</Th>
                    <Th>Creata</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <SignupRow key={row.id} row={row} onCancel={() => setConfirming(row)} />
                  ))}
                </Tbody>
              </Table>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground tabular-nums">
                Pagina {page} di {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1 || listQ.isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prec.
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= totalPages || listQ.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Succ.
                </Button>
                {listQ.isFetching && (
                  <Loader2
                    className="h-4 w-4 animate-spin text-muted-foreground"
                    strokeWidth={1.5}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </PageContent>

      <Dialog
        open={confirming !== null}
        onOpenChange={(o) => {
          if (!o) setConfirming(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminare la registrazione?</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2 text-sm">
            <p>
              Il checkout in attesa per <span className="font-mono">{confirming?.slug}</span> verrà{" "}
              <strong>eliminato definitivamente</strong> e lo slug tornerà disponibile.
            </p>
            {cancel.isError && (
              <p className="text-danger">
                {cancel.error instanceof PlatformApiError
                  ? cancel.error.message
                  : "Operazione non riuscita."}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Indietro
            </Button>
            <Button
              variant="danger"
              disabled={cancel.isPending}
              onClick={() => confirming && cancel.mutate(confirming.id)}
            >
              {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function SignupRow({ row, onCancel }: { row: PendingSignup; onCancel: () => void }) {
  return (
    <Tr>
      <Td className="font-mono text-xs">{row.slug}</Td>
      <Td>
        {/* An extra workspace is NOT a new organization — same table, different intent
            (ADR-0079), and reading one as the other inflates the funnel. */}
        <Badge tone={row.kind === "workspace" ? "draft" : "neutral"}>
          {row.kind === "workspace" ? "Workspace" : "Registrazione"}
        </Badge>
      </Td>
      <Td className="text-sm">{row.company_name}</Td>
      <Td className="text-sm">{row.admin_email}</Td>
      <Td className="text-sm">
        {row.plan_key ?? <span className="text-muted-foreground">—</span>}
      </Td>
      <Td>
        <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
      </Td>
      <Td className="whitespace-nowrap text-xs text-muted-foreground">
        {new Date(row.created_at).toLocaleString()}
      </Td>
      <Td className="text-right">
        {row.status === "pending" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            aria-label={`Elimina la registrazione di ${row.slug}`}
          >
            <Trash2 className="h-4 w-4 text-danger" strokeWidth={1.5} />
          </Button>
        )}
      </Td>
    </Tr>
  );
}
